import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import BulkImport from "@/components/BulkImport";
import { Card, PageHeader, Modal, Button, Field, Input, Textarea, Select } from "@/components/ui-kit";
import BatchingNamePicker from "@/components/BatchingNamePicker";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { Truck, Plus, Trash2, Pencil, Eye, AlertTriangle, CheckCircle2, RefreshCw, Lock, Printer, Filter, X } from "lucide-react";
import PdfViewerModal from "@/components/pdf/PdfViewerModal";
import { todayISO, parseQty, formatNumber } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { DatePicker } from "@/components/DatePicker";
import { useTableFilter } from "@/hooks/useTableFilter";

const blankItem = { mutu_beton: "K-300 Non-Fly Ash", slump: "12 ± 2 cm", volume_m3: 0, keterangan: "" };
const blankForm = {
  sj_number: "", date: todayISO(), pelanggan: "", proyek: "", no_po_kontrak: "",
  no_truk: "", sopir: "", items: [{ ...blankItem }],
  catatan_penting: "Penambahan air di lokasi adalah tanggung jawab pembeli. Batas waktu tunggu bongkar adalah 2 jam. Lebih dari itu dikenakan biaya demorage.",
  waktu_berangkat: "", waktu_tiba: "", waktu_mulai_cor: "", waktu_selesai_cor: "",
  name_penerima: "", name_sopir: "", name_dispatcher: "",
};

export default function BatchingSj() {
  const { hasPerm } = useAuth();
  const canEditNumber = hasPerm("batching.sj.edit_number");
  const canCreate = hasPerm("batching.sj.create");
  // Iter v34cv (#1) — Cetak blanko SJ Readymix.
  const canPrintBlank = hasPerm("batching.sj_readymix.print_blank");
  const [blankOpen, setBlankOpen] = useState(false);
  const [blankCopies, setBlankCopies] = useState(2);
  const [blankPdfOpen, setBlankPdfOpen] = useState(false);
  const canEdit = hasPerm("batching.sj.edit");
  const canDelete = hasPerm("batching.sj.delete");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  // Iter Fase B — masters untuk autocomplete (datalist).
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [mixes, setMixes] = useState([]);
  // Iter 60 — clients master untuk dropdown Pelanggan + Batching POs untuk dropdown No. PO/Kontrak.
  const [clients, setClients] = useState([]);
  const [batchingPos, setBatchingPos] = useState([]);
  // Iter 61 — Task 6: PO Rekap Kuota dengan kategori "Batching Plant".
  const [rekapPos, setRekapPos] = useState([]);
  // Iter v34bl — Master lokasi proyek untuk dropdown "Lokasi Proyek".
  const [locations, setLocations] = useState([]);
  // Iter v34z #3 Phase C — live preview of auto-deduct
  const [preview, setPreview] = useState({ deductions: [], skipped: [], stock_status: "ok", insufficient: [] });
  const previewTimer = useRef(null);
  // Iter 64 (#9) — Popout PDF viewer for batching SJ.
  const [pdfViewerSj, setPdfViewerSj] = useState(null);

  const load = () => api.get("/batching/sj").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
    api.get("/batching/drivers").then((r) => setDrivers(r.data || [])).catch(() => {});
    api.get("/batching/trucks", { params: { truck_type: "readymix" } }).then((r) => setTrucks(r.data || [])).catch(() => {});
    api.get("/batching/mix-designs").then((r) => setMixes(r.data || [])).catch(() => {});
    // Iter 60+61 — pelanggan + batching PO + PO Rekap Kuota (category 'Batching Plant') autocomplete sources
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/batching/pos").then((r) => setBatchingPos(r.data || [])).catch(() => {});
    // Iter 61 — Task 6: PO Rekap Kuota dengan kategori "Batching Plant" juga muncul di datalist No. PO/Kontrak.
    api.get("/pos").then((r) => {
      // Iter v34bl (Task #2) — Sembunyikan PO yang sudah selesai ditagih penuh dari dropdown.
      // Iter v34br (Task 10) — Sembunyikan PO yang di-tutup manual (is_closed).
      const all = (r.data || []).filter((p) => !p.is_closed && (p.category || "").toLowerCase().trim() === "batching plant");
      const notFullyBilled = all.filter((p) => {
        const t = Number(p.grand_total ?? p.total_value ?? p.po_total) || 0;
        const b = Number(p.billed_total ?? p.billed_amount) || 0;
        const cnt = Number(p.billed_count) || 0;
        const allowed = Number(p.billing_count_allowed) || 0;
        // Habis kuota bila (a) total_billed >= grand_total DAN grand > 0, atau
        // (b) billed_count >= billing_count_allowed DAN allowed > 0.
        const byValue = t > 0 && b >= t - 0.01;
        const byCount = allowed > 0 && cnt >= allowed;
        return !(byValue || byCount);
      });
      setRekapPos(notFullyBilled);
    }).catch(() => {});
    // Iter v34bl — Master locations
    api.get("/batching/locations").then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  // Iter v34z #3 Phase C — debounce preview fetch when items change
  useEffect(() => {
    if (!open) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const items = form.items
          .filter((i) => i.mutu_beton && parseQty(i.volume_m3) > 0)
          .map((i) => ({ ...i, volume_m3: parseQty(i.volume_m3) }));
        if (items.length === 0) { setPreview({ deductions: [], skipped: [], stock_status: "ok", insufficient: [] }); return; }
        const r = await api.post("/batching/sj/preview-deduction", { items });
        setPreview(r.data || { deductions: [], skipped: [], stock_status: "ok", insufficient: [] });
      } catch { /* silent */ }
    }, 400);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [form.items, open]);

  const setItem = (i, k, v) => { const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items }); };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...blankItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });

  const openNew = async () => {
    setEditId(null);
    setForm({ ...blankForm, items: [{ ...blankItem }] });
    setOpen(true);
    // Iter v34z (#4) — auto-fetch next SJ number on open
    try {
      const r = await api.get("/batching/sj/next-number");
      if (r.data?.sj_number) setForm((f) => ({ ...f, sj_number: r.data.sj_number }));
    } catch { /* silent */ }
  };
  const suggestSjNumber = async () => {
    try {
      const r = await api.get("/batching/sj/next-number");
      if (r.data?.sj_number) setForm((f) => ({ ...f, sj_number: r.data.sj_number }));
    } catch { toast.error("Gagal mengambil nomor SJ"); }
  };
  const openEdit = (sj) => {
    setEditId(sj.id);
    setForm({ ...blankForm, ...sj, name_sopir: sj.name_sopir || sj.sopir || "", items: (sj.items || [{ ...blankItem }]).map((x) => ({ ...x })) });
    setOpen(true);
  };

  const submit = async () => {
    // sj_number is auto-filled — only fail if pelanggan or items missing.
    if (!form.pelanggan || !form.items[0]?.mutu_beton) {
      toast.error("Pelanggan dan minimal 1 item wajib diisi"); return;
    }
    setSaving(true);
    try {
      // Iter v34cy — field sopir kini satu sumber: "Nama Sopir (cetak)".
      const sopirName = (form.name_sopir || form.sopir || "").trim();
      const payload = {
        ...form, sopir: sopirName, name_sopir: sopirName,
        items: form.items.map((i) => ({ ...i, volume_m3: parseQty(i.volume_m3) })),
      };
      if (editId) await api.patch(`/batching/sj/${editId}`, payload);
      else await api.post("/batching/sj", payload);
      toast.success(editId ? "SJ diupdate" : "SJ Batching dibuat");
      setOpen(false); setEditId(null); setForm(blankForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus SJ ini?")) return;
    try { await api.delete(`/batching/sj/${id}`); load(); toast.success("SJ dihapus"); }
    catch (e) { toast.error("Gagal hapus"); }
  };

  // Iter v34cz (#4) — Filter & pencarian kombinasi kata (seperti Rekap Surat Jalan).
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fPelanggan, setFPelanggan] = useState("");
  const [fMutu, setFMutu] = useState("");
  const [fStock, setFStock] = useState("");
  const pelangganOpts = useMemo(
    () => [...new Set(rows.map((r) => (r.pelanggan || "").trim()).filter(Boolean))].sort(),
    [rows],
  );
  const mutuOpts = useMemo(() => {
    const s = new Set(mixes.map((m) => m.name));
    rows.forEach((r) => (r.items || []).forEach((i) => i.mutu_beton && s.add(i.mutu_beton)));
    return [...s].sort();
  }, [rows, mixes]);
  const preFiltered = useMemo(() => rows.filter((r) => {
    const d = r.date || "";
    if (fFrom && d < fFrom) return false;
    if (fTo && d > fTo) return false;
    if (fPelanggan && (r.pelanggan || "") !== fPelanggan) return false;
    if (fMutu && !(r.items || []).some((i) => (i.mutu_beton || "") === fMutu)) return false;
    if (fStock && (r.stock_status || "") !== fStock) return false;
    return true;
  }), [rows, fFrom, fTo, fPelanggan, fMutu, fStock]);
  const { filtered, FilterBar, q, setQ } = useTableFilter(
    preFiltered,
    ["sj_number", "date", "pelanggan", "proyek", "no_truk", "sopir", "no_po_kontrak", "items.mutu_beton", "items.keterangan"],
    "Cari SJ / pelanggan / proyek / truk / mutu…",
  );
  const totalVolumeFiltered = useMemo(
    () => filtered.reduce((s, r) => s + (Number(r.total_volume_m3) || 0), 0),
    [filtered],
  );
  const resetFilters = () => { setFFrom(""); setFTo(""); setFPelanggan(""); setFMutu(""); setFStock(""); setQ(""); };
  const hasFilter = !!(fFrom || fTo || fPelanggan || fMutu || fStock || q);

  return (
    <div className="space-y-6">
      <PageHeader overline="Batching Plant" title="Surat Jalan Readymix" description="Cetak Surat Jalan / Delivery Note readymix concrete ke proyek pelanggan." icon={Truck}
        action={(canCreate || canPrintBlank) ? (
          <div className="flex items-center gap-2">
            {canCreate && <BulkImport kind="batching_sj_rm" onDone={load} />}
            {/* Iter v34cv (#1) — Cetak SJ Readymix kosongan (blanko). */}
            {canPrintBlank && (
              <Button variant="secondary" onClick={() => setBlankOpen(true)} data-testid="rm-sj-blank-open">
                <Printer className="h-3.5 w-3.5" /> Cetak SJ Kosongan
              </Button>
            )}
            {canCreate && <Button onClick={openNew} data-testid="batching-sj-new-btn"><Plus className="h-4 w-4" /> SJ Baru</Button>}
          </div>
        ) : null} />
      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-stone-600 mr-1">
            <Filter className="h-3.5 w-3.5" /> Filter
          </div>
          <Field label="Dari"><DatePicker value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="rm-sj-filter-from" /></Field>
          <Field label="Sampai"><DatePicker value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="rm-sj-filter-to" /></Field>
          <Field label="Pelanggan">
            <Select value={fPelanggan} onChange={(e) => setFPelanggan(e.target.value)} data-testid="rm-sj-filter-pelanggan">
              <option value="">— Semua —</option>
              {pelangganOpts.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Mutu Beton">
            <Select value={fMutu} onChange={(e) => setFMutu(e.target.value)} data-testid="rm-sj-filter-mutu">
              <option value="">— Semua —</option>
              {mutuOpts.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
          <Field label="Status Stok">
            <Select value={fStock} onChange={(e) => setFStock(e.target.value)} data-testid="rm-sj-filter-stock">
              <option value="">— Semua —</option>
              <option value="ok">OK</option>
              <option value="partial">Sebagian Kurang</option>
              <option value="insufficient">Kurang</option>
            </Select>
          </Field>
          {hasFilter && (
            <Button variant="ghost" onClick={resetFilters} data-testid="rm-sj-filter-reset"><X className="h-3.5 w-3.5" /> Reset</Button>
          )}
          <div className="ml-auto flex items-center gap-3">
            {FilterBar}
            <span className="text-xs text-stone-500 whitespace-nowrap" data-testid="rm-sj-total-volume">
              Total: <b className="font-mono-num">{formatNumber(totalVolumeFiltered, 2)}</b> m³
            </span>
          </div>
        </div>
      </Card>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-600">
            <tr><th className="text-left py-2 px-3">No SJ</th><th className="text-left py-2 px-3">Tanggal</th><th className="text-left py-2 px-3">Pelanggan</th><th className="text-left py-2 px-3">Proyek</th><th className="text-left py-2 px-3">Truk / Sopir</th><th className="text-right py-2 px-3">Volume (m³)</th><th className="text-center py-2 px-3">Stok</th><th className="text-right py-2 px-3 w-28">Aksi</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center text-stone-400 py-8 text-xs">{rows.length ? "Tidak ada SJ sesuai filter/pencarian" : "Belum ada SJ"}</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/40" data-testid={`batching-sj-row-${r.id}`}>
                <td className="py-2 px-3 font-mono">{r.sj_number}</td>
                <td className="py-2 px-3">{r.date}</td>
                <td className="py-2 px-3">{r.pelanggan}</td>
                <td className="py-2 px-3 text-stone-600">{r.proyek || "-"}</td>
                <td className="py-2 px-3 text-stone-600">{r.no_truk || "-"} / {r.sopir || "-"}</td>
                <td className="py-2 px-3 text-right font-mono">{formatNumber(r.total_volume_m3 || 0, 2)}</td>
                <td className="py-2 px-3 text-center text-[10px]">
                  {r.stock_status === "ok" && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold uppercase">OK</span>}
                  {r.stock_status === "partial" && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold uppercase">Sebagian</span>}
                  {r.stock_status === "insufficient" && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold uppercase">Kurang</span>}
                  {!r.stock_status && <span className="text-stone-400">—</span>}
                </td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => setPdfViewerSj(r)} className="text-stone-500 hover:text-terra-600 px-1.5 inline-block" title="Lihat PDF" data-testid={`batching-sj-pdf-${r.id}`}><Eye className="h-3.5 w-3.5" /></button>
                  {canEdit && (
                    <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-600 px-1.5" data-testid={`batching-sj-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                  {canDelete && (
                    <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-1.5" data-testid={`batching-sj-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Surat Jalan" : "Surat Jalan Baru"} size="xl">
        <datalist id="batching-mix-list">{mixes.map((m) => <option key={m.id} value={m.name} />)}</datalist>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="No. SJ" required hint={canEditNumber ? "Auto-generate, dapat diedit (Anda punya izin)" : "Auto-generate (hanya role tertentu yang boleh edit)"}>
              <div className="flex gap-1">
                <Input className="flex-1 font-mono" value={form.sj_number} onChange={(e) => canEditNumber && setForm({ ...form, sj_number: e.target.value })} readOnly={!canEditNumber} placeholder="SJ-RMX-2606-0001" data-testid="batching-sj-number" />
                {canEditNumber ? (
                  <Button variant="secondary" type="button" onClick={suggestSjNumber} className="!px-2" title="Ambil nomor berikutnya" data-testid="batching-sj-suggest-num">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  <span className="flex items-center px-2 text-stone-400" title="Anda tidak punya izin edit nomor SJ"><Lock className="h-3.5 w-3.5" /></span>
                )}
              </div>
            </Field>
            <Field label="Tanggal" required><DatePicker value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
            <Field label="No. PO / Kontrak" hint="Pilih dari PO Batching / PO Rekap Kuota (yang belum habis kuota)">
              <Select value={form.no_po_kontrak} onChange={(e) => setForm({ ...form, no_po_kontrak: e.target.value })} data-testid="batching-sj-po-kontrak">
                <option value="">— Pilih PO —</option>
                {batchingPos.map((po) => (
                  <option key={`bp-${po.id}`} value={po.po_number}>{po.project ? `${po.po_number} — ${po.project}` : po.po_number} (PO Batching)</option>
                ))}
                {rekapPos.map((po) => (
                  <option key={`rp-${po.id}`} value={po.po_number}>{po.client_name ? `${po.po_number} — ${po.client_name}` : po.po_number} (Rekap Kuota)</option>
                ))}
                {/* Fallback bila nilai existing (edit) tidak ada di dropdown */}
                {form.no_po_kontrak && !batchingPos.some(po => po.po_number === form.no_po_kontrak) && !rekapPos.some(po => po.po_number === form.no_po_kontrak) && (
                  <option value={form.no_po_kontrak}>{form.no_po_kontrak} (lama)</option>
                )}
              </Select>
            </Field>
            {/* Iter v34cy — Pelanggan & Lokasi Proyek: ketik manual + dropdown riwayat (maks 10) */}
            <Field label="Pelanggan" required hint="Ketik manual — dropdown menampilkan 10 riwayat terakhir">
              <AutocompleteInput field="batching.sj.pelanggan" limit={10} value={form.pelanggan}
                onChange={(v) => setForm({ ...form, pelanggan: v })}
                placeholder="Ketik nama pelanggan" dataTestid="batching-sj-pelanggan" />
            </Field>
            <Field label="Lokasi Proyek" hint="Ketik manual — dropdown menampilkan 10 riwayat terakhir">
              <AutocompleteInput field="batching.sj.proyek" limit={10} value={form.proyek}
                onChange={(v) => setForm({ ...form, proyek: v })}
                placeholder="Ketik lokasi proyek" dataTestid="batching-sj-lokasi" />
            </Field>
            {/* Iter v34cy — Sopir digabung ke "Nama Sopir (cetak)" di bawah; di sini hanya truk. */}
            <Field label="No. Truk">
              <Select value={form.no_truk} onChange={(e) => setForm({ ...form, no_truk: e.target.value })} data-testid="batching-sj-truk">
                <option value="">— Pilih Truk —</option>
                {trucks.map((t) => <option key={t.id} value={t.plate}>{t.plate} — {t.capacity_m3} m³</option>)}
                {form.no_truk && !trucks.some((t) => t.plate === form.no_truk) && (
                  <option value={form.no_truk}>{form.no_truk} (lama)</option>
                )}
              </Select>
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold uppercase text-stone-600">Item Pengiriman</span>
              <Button variant="secondary" className="!py-1 !px-2 text-xs" onClick={addItem}><Plus className="h-3 w-3" /> Tambah</Button>
            </div>
            <div className="border border-stone-200">
              <div className="gap-1.5 p-2 bg-stone-50 text-[10px] uppercase tracking-wider font-semibold text-stone-600 border-b border-stone-200" style={{ display: "grid", gridTemplateColumns: "3fr 1.5fr 1.2fr 2fr 0.4fr" }}>
                <div>Mutu Beton (Mix Design)</div><div>Slump</div><div className="text-right">Volume (m³)</div><div>Keterangan</div><div></div>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="gap-1.5 p-2 border-b border-stone-100 items-center" style={{ display: "grid", gridTemplateColumns: "3fr 1.5fr 1.2fr 2fr 0.4fr" }}>
                  {/* Iter v34bl (Task #4) — Mutu Beton dropdown dari master mix designs. */}
                  <Select value={it.mutu_beton} onChange={(e) => setItem(i, "mutu_beton", e.target.value)} data-testid={`batching-sj-item-mutu-${i}`}>
                    <option value="">— Pilih Mutu Beton —</option>
                    {mixes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
                    {it.mutu_beton && !mixes.some(m => m.name === it.mutu_beton) && (
                      <option value={it.mutu_beton}>{it.mutu_beton} (lama)</option>
                    )}
                  </Select>
                  {/* Iter v34bl (Task #5) — Slump input + tombol insert simbol ±. */}
                  <div className="flex gap-1">
                    <Input value={it.slump} onChange={(e) => setItem(i, "slump", e.target.value)} className="flex-1" data-testid={`batching-sj-item-slump-${i}`}/>
                    <button
                      type="button"
                      onClick={() => setItem(i, "slump", (it.slump || "") + "±")}
                      className="px-2 border border-stone-300 hover:bg-terra-50 hover:border-terra-300 text-stone-700 text-sm font-bold shrink-0"
                      title="Sisipkan simbol ±"
                      data-testid={`batching-sj-item-slump-pm-${i}`}
                    >±</button>
                  </div>
                  <Input className="text-right font-mono-num" value={it.volume_m3} onChange={(e) => setItem(i, "volume_m3", e.target.value)} data-testid={`batching-sj-item-vol-${i}`} />
                  <Input value={it.keterangan} onChange={(e) => setItem(i, "keterangan", e.target.value)} placeholder="Segel #8821" />
                  <button onClick={() => rmItem(i)} className="text-stone-400 hover:text-red-700 justify-self-center"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
          <Field label="Catatan Penting (dicetak di SJ)"><Textarea rows={2} value={form.catatan_penting} onChange={(e) => setForm({ ...form, catatan_penting: e.target.value })} /></Field>

          {/* Iter v34z #3 Phase C — preview auto-deduct material stock */}
          {(preview.deductions.length > 0 || preview.skipped.length > 0) && (
            <div className="border border-stone-200 rounded-md bg-stone-50/60 p-3" data-testid="sj-deduct-preview">
              <div className="flex items-center gap-2 mb-2">
                {preview.stock_status === "ok" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                {preview.stock_status !== "ok" && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                <div className="text-xs uppercase tracking-wider font-semibold text-stone-700">
                  Material yang akan dipotong dari stok
                </div>
                {preview.stock_status === "insufficient" && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded font-semibold uppercase">Semua kurang</span>
                )}
                {preview.stock_status === "partial" && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold uppercase">Sebagian kurang</span>
                )}
                {preview.stock_status === "ok" && (
                  <span className="ml-auto text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold uppercase">Stok cukup</span>
                )}
              </div>
              {preview.deductions.length > 0 && (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-stone-500 border-b border-stone-200">
                      <th className="py-1 px-1.5">Material</th>
                      <th className="py-1 px-1.5 text-right">Kebutuhan (kg)</th>
                      <th className="py-1 px-1.5 text-right">Stok Sekarang (kg)</th>
                      <th className="py-1 px-1.5 text-right">Selisih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.deductions.map((d) => {
                      const short = d.short_kg > 0.001;
                      return (
                        <tr key={d.material_id} className="border-b border-stone-100 last:border-0" data-testid={`sj-deduct-row-${d.material_id}`}>
                          <td className="py-1 px-1.5 font-medium text-stone-700">{d.material_name}</td>
                          <td className="py-1 px-1.5 text-right font-mono-num">{formatNumber(d.needed_kg)}</td>
                          <td className="py-1 px-1.5 text-right font-mono-num text-stone-600">{formatNumber(d.balance_kg)}</td>
                          <td className={`py-1 px-1.5 text-right font-mono-num font-semibold ${short ? "text-red-600" : "text-emerald-600"}`}>
                            {short ? `−${formatNumber(d.short_kg)}` : "✓"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {preview.skipped.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5 border border-amber-200">
                  <strong>⚠ Mutu beton tanpa Mix Design ({preview.skipped.length}):</strong>{" "}
                  {preview.skipped.map((s) => `${s.mutu_beton} (${s.volume_m3} m³)`).join(", ")}
                  {" — "}auto-deduct tidak akan jalan untuk item ini. Setup composition di <strong>Master Batching</strong>.
                </div>
              )}
              {preview.stock_status !== "ok" && preview.deductions.length > 0 && (
                <div className="mt-2 text-[11px] text-amber-700">
                  💡 SJ tetap bisa disimpan; status akan ditandai <code>stock_status: &quot;{preview.stock_status}&quot;</code> untuk audit. Saldo material yang kurang akan menjadi negatif.
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Berangkat Plant"><Input value={form.waktu_berangkat} onChange={(e) => setForm({ ...form, waktu_berangkat: e.target.value })} placeholder="08:30" /></Field>
            <Field label="Tiba di Proyek"><Input value={form.waktu_tiba} onChange={(e) => setForm({ ...form, waktu_tiba: e.target.value })} placeholder="09:15" /></Field>
            <Field label="Mulai Pengecoran"><Input value={form.waktu_mulai_cor} onChange={(e) => setForm({ ...form, waktu_mulai_cor: e.target.value })} placeholder="09:30" /></Field>
            <Field label="Selesai Pengecoran"><Input value={form.waktu_selesai_cor} onChange={(e) => setForm({ ...form, waktu_selesai_cor: e.target.value })} placeholder="10:00" /></Field>
          </div>
          {/* Iter 55 / v34bd — Nama Penandatangan (Penerima / Sopir / Pengirim=Dispatcher) diambil dari Master Batching */}
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Nama Penandatangan</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Penerima / Site Manager">
                <BatchingNamePicker source="receivers" value={form.name_penerima}
                  onPick={({ name }) => setForm({ ...form, name_penerima: name })}
                  data-testid="batching-sj-name-penerima" />
              </Field>
              <Field label="Nama Sopir (cetak)" hint="Dipakai juga sebagai sopir truk pada rekap SJ">
                <BatchingNamePicker source="drivers" value={form.name_sopir}
                  onPick={({ name }) => setForm({ ...form, name_sopir: name })}
                  data-testid="batching-sj-name-sopir" />
              </Field>
              <Field label="Dispatcher / Pengirim">
                <BatchingNamePicker source="senders" value={form.name_dispatcher}
                  onPick={({ name }) => setForm({ ...form, name_dispatcher: name })}
                  data-testid="batching-sj-name-dispatcher" />
              </Field>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} data-testid="batching-sj-save-btn">{saving ? "Menyimpan…" : (editId ? "Update SJ" : "Simpan SJ")}</Button>
          </div>
        </div>
      </Modal>

      {/* Iter 64 (#9) — Popout PDF viewer for Batching SJ. */}
      <PdfViewerModal
        open={!!pdfViewerSj}
        onClose={() => setPdfViewerSj(null)}
        pdfUrl={pdfViewerSj ? `${process.env.REACT_APP_BACKEND_URL}/api/batching/sj/${pdfViewerSj.id}/pdf` : ""}
        fileName={`SJ-Batching-${pdfViewerSj?.sj_number || pdfViewerSj?.id || "doc"}.pdf`}
        title={`Surat Jalan ${pdfViewerSj?.sj_number || ""}`}
      />

      {/* Iter v34cv (#1) — Dialog jumlah lembar SJ Readymix kosongan */}
      <Modal open={blankOpen} onClose={() => setBlankOpen(false)} title="Cetak Surat Jalan Readymix Kosongan" size="sm">
        <div className="space-y-4">
          <p className="text-xs text-stone-600">
            Mencetak blanko Surat Jalan Readymix dengan kop perusahaan dan isian kosong
            (Mutu Beton, Slump, Volume, waktu Berangkat/Tiba/Cor, tanda tangan) untuk diisi tangan.
            Tidak memotong stok dan tidak memakai nomor SJ. 1 lembar = 1 halaman.
          </p>
          <Field label="Jumlah Lembar" hint="Maksimal 50 lembar per cetak">
            <Input type="number" min="1" max="50" value={blankCopies}
              onChange={(e) => setBlankCopies(e.target.value)} className="font-mono-num"
              data-testid="rm-sj-blank-copies" />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setBlankOpen(false)}>Batal</Button>
            <Button onClick={() => { setBlankOpen(false); setBlankPdfOpen(true); }} data-testid="rm-sj-blank-print">
              <Printer className="h-3.5 w-3.5" /> Buka PDF
            </Button>
          </div>
        </div>
      </Modal>

      <PdfViewerModal
        open={blankPdfOpen}
        onClose={() => setBlankPdfOpen(false)}
        pdfUrl={blankPdfOpen ? `${process.env.REACT_APP_BACKEND_URL}/api/batching/sj/blank-pdf?copies=${Math.max(1, Math.min(Number(blankCopies) || 1, 50))}` : ""}
        fileName={`SJ-Readymix-Kosongan-${blankCopies}lembar.pdf`}
        title="Surat Jalan Readymix — Kosongan"
      />

    </div>
  );
}
